import React from 'react'
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid, InputAdornment } from '@mui/material';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { Auth } from '../../../Component/Api/Auth/AuthService';
import { MFARequest, PasswordRequest, SecurityInfo } from '../../../Component/Model/SignIn';
import { defaultObject } from '../../../Helpers/typescriptHelper';
import { SmallLoading } from '../../../Component/Common/Components/Loading/IntermediateLoading';
import AppTextInput from '../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { isArray } from 'lodash';
import { TwoFactorContent } from '../TwoFactor/TwoFactorContent';
import { passwordValidation } from './PasswordValidation';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';

export const SecurityDetails = () => {
    const [securityInfo, setSecurityInfo] = React.useState<SecurityInfo>(defaultObject);
    const [loading, setLoading] = React.useState<boolean>(false);

    const [ShowEnableTwoFactorAuthentication, setShowEnableTwoFactorAuthentication] = React.useState<boolean>(false);
    const [ShowAddTwoFactorAuthenticationDevice, setShowAddTwoFactorAuthenticationDevice] = React.useState<boolean>(false);
    const [ShowCurrentTwoFactorAuthenticationConfiguration, setShowCurrentTwoFactorAuthenticationConfiguration] = React.useState<boolean>(false);
    const [ShowCancelAddTwoFactorAuthenticationDevice, setShowCancelAddTwoFactorAuthenticationDevice] = React.useState<boolean>(false);
    const [TwoFactorButtonText, setTwoFactorButtonText] = React.useState<string>("Enable Two Factor Authentication");

    const [isNewPasswordVisible, setIsNewPasswordVisible] = React.useState(false);
    const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = React.useState(false);


    const { control, handleSubmit, formState: { isSubmitting } } = useForm({
        mode: 'all',
        resolver: yupResolver(passwordValidation)
    })

    React.useEffect(() => {
        setLoading(true);
        try {
            Auth.getSecurityInfo().then(result => {
                LoadSecurityData(result.data);
            })
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleShowNewPassword = () => {
        setIsNewPasswordVisible(!isNewPasswordVisible);
    }

    const handleShowConfirmPassword = () => {
        setIsConfirmPasswordVisible(!isConfirmPasswordVisible);
    }

    const LoadSecurityData = (data: SecurityInfo) => {
        setSecurityInfo(data);

        const ShowCurrentTwoFactorAuthenticationConfigurations = data.IsTwoFactorAuthenticationSetup || (!data.CanChangeEnableTwoFactorAuthentication && !data.IsTwoFactorAuthenticationSetup);
        setShowCurrentTwoFactorAuthenticationConfiguration(ShowCurrentTwoFactorAuthenticationConfigurations);

        const showEnableTwoFactorAuthentication = !data.IsTwoFactorAuthenticationSetup && data.CanChangeEnableTwoFactorAuthentication;
        setShowEnableTwoFactorAuthentication(showEnableTwoFactorAuthentication);

        if (data.IsTwoFactorAuthenticationSetup) {
            setTwoFactorButtonText('Update Two Factor Authentication Device');
        } else {
            setTwoFactorButtonText('Enable Two Factor Authentication');
        }
    }

    const OnChangeTwoFactorAuthenticationDeviceClick = () => {
        setShowAddTwoFactorAuthenticationDevice(true);
        setShowCancelAddTwoFactorAuthenticationDevice(true);
        setShowCurrentTwoFactorAuthenticationConfiguration(false);
    }
    const EnableTwoFactorCardFunction = () => {
        setShowEnableTwoFactorAuthentication(false);
        setShowAddTwoFactorAuthenticationDevice(true);
        setShowCancelAddTwoFactorAuthenticationDevice(securityInfo.CanChangeEnableTwoFactorAuthentication);
    }

    const CancelTwoFactorCard = () => {
        setShowAddTwoFactorAuthenticationDevice(false);
        setShowEnableTwoFactorAuthentication(true);
    }

    const OnCancelAddTwoFactorAuthenticationDeviceClick = () => {
        setShowAddTwoFactorAuthenticationDevice(false);
        if (securityInfo.IsTwoFactorAuthenticationSetup) {
            setShowCurrentTwoFactorAuthenticationConfiguration(true);
        } else {
            setShowEnableTwoFactorAuthentication(!securityInfo.IsTwoFactorAuthenticationSetup);
        }
    }


    const handlePostTFARequest = (request: MFARequest) => {
        Auth.addTwoFactorAuthentication(request).then(result => {
            LoadSecurityData(result.data);
            setShowAddTwoFactorAuthenticationDevice(false);
        }).catch(error => {
            toast.error(error.response.data.message);
        });
    }

    const handleDataSubmit = async (data: FieldValues) => {
        const passwordRequest: PasswordRequest = {
            OldPassword: data.oldPassword,
            NewPassword: data.newPassword,
            ConfirmPassword: data.confirmNewPassword
        }

        try {
            const passwordChange = await Auth.changePassword(passwordRequest);
            if (passwordChange.status === 200) {
                toast.success('Password Changed');
            }
        } catch (error: any) {
            console.log(error);
            if (isArray(error)) {
                let Errors: string[] = error;
                Errors.forEach((error: string) => {
                    toast.error(error);
                });
            }
        }
    }

    if (loading) return <SmallLoading />;
    return (
        <>
            <form id='SecurityDetailsForm' onSubmit={handleSubmit(handleDataSubmit)}>
                <Grid container mb={2}>
                    <Grid item xs={12} >
                        <AppTextInput label="Old Password" name="oldPassword" type="password" control={control} />
                    </Grid>
                </Grid>
                <Grid container mb={2}>
                    <Grid item xs={12} >
                        <AppTextInput label="New Password" name="newPassword" control={control}  type={isNewPasswordVisible ? "text" : "password"}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                                        <span onClick={handleShowNewPassword}>{isNewPasswordVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}</span>
                                    </InputAdornment>
                                )}} 
                        />
                    </Grid>
                </Grid>
                <Grid container mb={2}>
                    <Grid item xs={12} >
                        <AppTextInput label="Confirm Password" name="confirmNewPassword" type={isConfirmPasswordVisible ? "text" : "password"} control={control}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                                        <span onClick={handleShowConfirmPassword}>{isConfirmPasswordVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}</span>
                                    </InputAdornment>
                                )}} 
                        />
                    </Grid>
                </Grid>
            </form>
            <Grid container mb={2}>
                <Grid item xs={12} sm={12} md={6}>
                    <LoadingButton form='SecurityDetailsForm' color="primary" variant="contained" loading={isSubmitting} type="submit">
                        Change Password
                    </LoadingButton>
                </Grid>
                {ShowEnableTwoFactorAuthentication ?
                    <Grid item xs={12} sm={12} md={6}>
                        <Button variant="contained" onClick={() => { EnableTwoFactorCardFunction() }}>
                            Enable Two Factor Authentication
                        </Button>
                    </Grid> : null}
            </Grid>

            <TwoFactorContent
                CancelTwoFactorCard={CancelTwoFactorCard}
                LoadSecurityData={LoadSecurityData}
                OnCancelAddTwoFactorAuthenticationDeviceClick={OnCancelAddTwoFactorAuthenticationDeviceClick}
                OnChangeTwoFactorAuthenticationDeviceClick={OnChangeTwoFactorAuthenticationDeviceClick}
                ShowAddTwoFactorAuthenticationDevice={ShowAddTwoFactorAuthenticationDevice}
                ShowCancelAddTwoFactorAuthenticationDevice={ShowCancelAddTwoFactorAuthenticationDevice}
                ShowCurrentTwoFactorAuthenticationConfiguration={ShowCurrentTwoFactorAuthenticationConfiguration}
                TwoFactorButtonText={TwoFactorButtonText}
                securityInfo={securityInfo}
                handleTFARequest={handlePostTFARequest} />
        </>
    )
}
